import React, { createContext, useContext, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { submitAppraisal } from "../lib/api";

const AppraisalContext = createContext(null);
export const useAppraisal = () => useContext(AppraisalContext);

const empty = {
  name: "",
  email: "",
  phone: "",
  property_type: "",
  location: "",
  tenancy_status: "",
  message: "",
};

export const AppraisalProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const openDialog = () => {
    setDone(false);
    setForm(empty);
    setOpen(true);
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please add your name, email, and mobile number.");
      return;
    }
    setLoading(true);
    try {
      await submitAppraisal(form);
      setDone(true);
      toast.success("Request received. We'll be in touch within one business day.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppraisalContext.Provider value={{ openDialog }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-lg bg-kept-sand border border-kept-ink/10 p-0 gap-0 rounded-none"
          data-testid="appraisal-dialog"
        >
          <div className="bg-kept-ink px-8 py-7 grain">
            <div className="kept-label text-kept-brass mb-2">Complimentary · No obligation</div>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl font-normal text-kept-sand tracking-tight">
                Book Your Free Rental Appraisal
              </DialogTitle>
              <DialogDescription className="text-kept-sand/60 text-sm mt-1">
                Tell us about your property. We reply within one business day.
              </DialogDescription>
            </DialogHeader>
          </div>

          {done ? (
            <div className="px-8 py-14 text-center" data-testid="appraisal-success">
              <div className="font-serif text-3xl text-kept-ink mb-3">Thank you.</div>
              <p className="text-muted-foreground text-sm max-w-xs mx-auto">
                Your appraisal request has reached our team. We'll follow up within one
                business day to arrange your call.
              </p>
              <button
                onClick={() => setOpen(false)}
                className="mt-8 kept-label border-b border-kept-brassdark pb-1 brass-text"
                data-testid="appraisal-close-btn"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="px-8 py-7 space-y-6" data-testid="appraisal-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <input
                  className="kept-input"
                  placeholder="Your name"
                  value={form.name}
                  onChange={(e) => set("name")(e.target.value)}
                  data-testid="appraisal-name"
                />
                <input
                  className="kept-input"
                  placeholder="Email address"
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email")(e.target.value)}
                  data-testid="appraisal-email"
                />
                <input
                  className="kept-input"
                  placeholder="Mobile number"
                  value={form.phone}
                  onChange={(e) => set("phone")(e.target.value)}
                  data-testid="appraisal-phone"
                />
                <input
                  className="kept-input"
                  placeholder="Location (city / suburb)"
                  value={form.location}
                  onChange={(e) => set("location")(e.target.value)}
                  data-testid="appraisal-location"
                />
                <Select value={form.property_type} onValueChange={set("property_type")}>
                  <SelectTrigger className="kept-input h-auto" data-testid="appraisal-property-type">
                    <SelectValue placeholder="Property type" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="Residential">Residential</SelectItem>
                    <SelectItem value="Commercial">Commercial</SelectItem>
                    <SelectItem value="Mixed-use">Mixed-use</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={form.tenancy_status} onValueChange={set("tenancy_status")}>
                  <SelectTrigger className="kept-input h-auto" data-testid="appraisal-tenancy">
                    <SelectValue placeholder="Tenancy status" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="Currently tenanted">Currently tenanted</SelectItem>
                    <SelectItem value="Vacant">Vacant</SelectItem>
                    <SelectItem value="Not sure">Not sure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <textarea
                className="kept-input resize-none"
                rows={2}
                placeholder="Your goals (optional)"
                value={form.message}
                onChange={(e) => set("message")(e.target.value)}
                data-testid="appraisal-message"
              />
              <motion.button
                type="submit"
                disabled={loading}
                whileTap={{ scale: 0.98 }}
                className="group w-full bg-kept-ink text-kept-brass py-4 kept-label flex items-center justify-center gap-3 disabled:opacity-60"
                data-testid="appraisal-submit"
              >
                {loading ? "Sending…" : "Request Appraisal"}
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </motion.button>
              <p className="text-center text-xs text-muted-foreground">
                No obligations. We'll follow up within one business day.
              </p>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </AppraisalContext.Provider>
  );
};
